import { useState, useEffect, useMemo } from 'react';
import { sentinelToEmpty } from '../../helpers/sentinelValue';
import firebase from "../../firebase/compat";
import saveFile from '../../hooks/SaveFile';
import { db, timestamp } from '../../firebase/config';
import { v4 as uuid } from 'uuid';
import { client } from '../../hooks/Client';
import Location from '../../hooks/Location';
import { useFirestoreGeneralOrderBy } from '../../firebase/useFirestore';
import languages from '../../utils/listOfLanguages';
import { useHistory } from "react-router-dom";
import UploadProgress from '../../components/common/UploadProgress.jsx';
import ProgressBar from '../../components/storytelling/ProgressBar';
import ReadOnlyGuard from '../../components/common/ReadOnlyGuard';

const AddOnstructuredInterview = () => {
  // State
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);
  const [audioFilePath, setAudioFilePath] = useState([]);
  const [text, setText] = useState('');
  const [structuredText, setStructuredText] = useState([]);
  const [momentId, setMomentId] = useState('');
  const [loading, setLoading] = useState('none');
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [transcriptionProgress, setTranscriptionProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [transcriptionLanguage, setTranscriptionLanguage] = useState('nl'); // Default to Dutch
  const [saved, setSaved] = useState(false);

  // Hooks
  const questionaireId = Location()[3];
  // Vanuit de bouwer zonder onderzoek kwam hier de string 'undefined' binnen (B5).
  const researchId = sentinelToEmpty(Location()[4]);
  const momentsRaw = useFirestoreGeneralOrderBy('MeasureMoments', 'ResearchID', researchId || '', 'Position', 'asc');
  const moments = useMemo(() => (momentsRaw || []).filter((m) => !m.Deleted), [momentsRaw]);
  const history = useHistory()

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setAudioFile(file);
      setAudioPreview(URL.createObjectURL(file));
    }
  };

  // -----------------------------
  // Helper Functions for Chunking
  // -----------------------------

  // Convert an AudioBuffer to a WAV Blob
  const audioBufferToWav = (buffer, opt) => {
    opt = opt || {};
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = opt.float32 ? 3 : 1;
    const bitDepth = format === 3 ? 32 : 16;

    let interleaved;
    if (numChannels === 2) {
      interleaved = interleave(
        buffer.getChannelData(0),
        buffer.getChannelData(1)
      );
    } else {
      interleaved = buffer.getChannelData(0);
    }
    return encodeWAV(interleaved, numChannels, sampleRate, bitDepth);
  };

  // Interleave two channels (for stereo audio)
  const interleave = (leftChannel, rightChannel) => {
    const length = leftChannel.length + rightChannel.length;
    const result = new Float32Array(length);
    let index = 0,
      inputIndex = 0;
    while (index < length) {
      result[index++] = leftChannel[inputIndex];
      result[index++] = rightChannel[inputIndex];
      inputIndex++;
    }
    return result;
  };

  // Encode PCM data into a WAV file Blob
  const encodeWAV = (samples, numChannels, sampleRate, bitDepth) => {
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;
    const buffer = new ArrayBuffer(44 + samples.length * bytesPerSample);
    const view = new DataView(buffer);

    // RIFF identifier
    writeString(view, 0, 'RIFF');
    // file length
    view.setUint32(4, 36 + samples.length * bytesPerSample, true);
    // RIFF type
    writeString(view, 8, 'WAVE');
    // format chunk identifier
    writeString(view, 12, 'fmt ');
    // format chunk length
    view.setUint32(16, 16, true);
    // sample format (raw PCM)
    view.setUint16(20, 1, true);
    // channel count
    view.setUint16(22, numChannels, true);
    // sample rate
    view.setUint32(24, sampleRate, true);
    // byte rate (sample rate * block align)
    view.setUint32(28, sampleRate * blockAlign, true);
    // block align (channel count * bytes per sample)
    view.setUint16(32, blockAlign, true);
    // bits per sample
    view.setUint16(34, bitDepth, true);
    // data chunk identifier
    writeString(view, 36, 'data');
    // data chunk length
    view.setUint32(40, samples.length * bytesPerSample, true);

    // Write the PCM samples
    if (bitDepth === 16) {
      let offset = 44;
      for (let i = 0; i < samples.length; i++, offset += 2) {
        const s = Math.max(-1, Math.min(1, samples[i]));
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
      }
    } else {
      // Extend this for 32-bit float if needed.
    }
    return new Blob([view], { type: 'audio/wav' });
  };

  // Write a string into the DataView (used for WAV header)
  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  // Split the audio file into chunks (chunkDuration in seconds)
  const splitAudioIntoChunks = async (file, chunkDuration = 5) => {
    const audioContext = new AudioContext();
    const arrayBuffer = await file.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const sampleRate = audioBuffer.sampleRate;
    const chunkSize = chunkDuration * sampleRate;
    const chunks = [];

    for (let start = 0; start < audioBuffer.length; start += chunkSize) {
      const end = Math.min(start + chunkSize, audioBuffer.length);
      const frameCount = end - start;
      const chunkBuffer = audioContext.createBuffer(
        audioBuffer.numberOfChannels,
        frameCount,
        sampleRate
      );

      for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
        // Get a slice of the channel data
        const channelData = audioBuffer.getChannelData(channel).slice(start, end);
        chunkBuffer.copyToChannel(channelData, channel, 0);
      }
      // Convert the chunk buffer to a WAV Blob
      const blob = audioBufferToWav(chunkBuffer);
      chunks.push(blob);
    }
    return chunks;
  };

  // -----------------------------
  // Helper Function to Upload a Chunk
  // -----------------------------
  const uploadChunk = (blob, chunkFileName) => {
    return new Promise((resolve, reject) => {
      const fileWithCustomName = new File([blob], chunkFileName, { type: 'audio/wav' });
      const syntheticEvent = { target: { files: [fileWithCustomName] } };
      saveFile(
        syntheticEvent,
        (downloadURL) => resolve(downloadURL),
        undefined,
        undefined,
        (err) => reject(err)
      );
    });
  };

  // -----------------------------
  // Updated Upload Function with Progress
  // -----------------------------
  const handleUpload = async () => {
    if (!audioFile) {
      alert('No file selected');
      return;
    }
    try {
      // Reset progress at start
      setUploadProgress(1);

      // Change chunkDuration to 120 seconds (2 minutes)
      const chunks = await splitAudioIntoChunks(audioFile, 120);
      console.log("Audio split into", chunks.length, "chunks");

      // Upload each chunk using the uploadChunk helper
      const chunkFilePaths = [];
      for (let i = 0; i < chunks.length; i++) {
        const chunkFileName = `${uuid()}_chunk_${i}.wav`;
        const downloadURL = await uploadChunk(chunks[i], chunkFileName);
        chunkFilePaths.push(downloadURL);

        // Update progress: (i+1) out of total chunks
        setUploadProgress(Math.round(((i + 1) / chunks.length) * 100));
      }
      // Save the array of download URLs to state
      setAudioFilePath(chunkFilePaths);
    } catch (error) {
      console.error("Error splitting and uploading audio:", error);
      setError("Er is iets fout gegaan bij het splitsen of uploaden.");
    }
  };

  // Speech-to-text function (process each chunk sequentially)
  const speech2Text = async () => {
    try {
      setLoading('block');
      setTranscriptionProgress(1);
      const speech2TextFunc = firebase.functions().httpsCallable('speech2Text');

      let fullTranscript = '';
      for (let i = 0; i < audioFilePath.length; i++) {
        const path = audioFilePath[i];
        const result = await speech2TextFunc({ data: path });
        fullTranscript += result.data.text + ' ';
        // Update transcription progress
        setTranscriptionProgress(Math.round(((i + 1) / audioFilePath.length) * 100));
      }
      setLoading('none');
      console.log(fullTranscript);
      setText(fullTranscript);
    } catch (error) {
      console.error(error.message);
      setText('Er is iets fout gegaan. Probeer het opnieuw.');
      setLoading('none');
    }
  };

  // Separate questions and answers with AI
  const seperateQuestions = async (e) => {

    e.target.innerText = 'Structureren...';
    setLoading('block');
    try {
      const genkit = firebase.functions().httpsCallable('genkit');
      const messages = [
        { role: "system", content: "Je bent een expert in het analyseren van ongestructureerde interviews en het scheiden van vragen en antwoorden." },
        { 
          role: "user", 
          content: `
          Analyseer deze tekst: "${text}".
          Identificeer de vragen die door de interviewer zijn gesteld en de antwoorden die door de geïnterviewde zijn gegeven.
          De tekst kan ongestructureerd zijn en bevat mogelijk meerdere vragen en antwoorden in één zin. Zorg ervoor dat je de vragen en antwoorden correct identificeert en structureert.
          Structureer de output als een JSON-array waarin elk item een object bevat met een vraag en een antwoord. Zorg ervoor dat het correct geformatteerd is voor directe JSON-parsing.

          Het verwachte formaat:
          [
              {"question": "Vraag 1", "answer": "Antwoord 1"},
              {"question": "Vraag 2", "answer": "Antwoord 2"},
              ...
          ]
          
          De tekst moet worden getranscribeert in deze taal: ${transcriptionLanguage}.
          Geef GEEN extra tekst, alleen het JSON-object.
          `
        }
      ];

      const prompt = `
      Je bent een expert in het analyseren van ongestructureerde interviews en het scheiden van vragen en antwoorden.
      Analyseer deze tekst: "${text}".
      Identificeer de vragen die door de interviewer zijn gesteld en de antwoorden die door de geïnterviewde zijn gegeven.
      Structureer de output als een JSON-array waarin elk item een object bevat met een vraag en een antwoord. Zorg ervoor dat
      De tekst kan ongestructureerd zijn en bevat mogelijk meerdere vragen en antwoorden in één zin. Zorg ervoor dat je de vragen en antwoorden correct identificeert en structureert. het correct geformatteerd is voor directe JSON-parsing.
      Het verwachte formaat:
      [
          {"question": "Vraag 1", "answer": "Antwoord 1"},
          {"question": "Vraag 2", "answer": "Antwoord 2"},
          ...
      ]

      De tekst moet vertaald worden in de taal met deze taalcode: ${transcriptionLanguage}.
      Geef GEEN extra tekst, alleen het JSON-object.
      `;

      const result = await genkit({ prompt: prompt });
      setResult(result);
      const answer = result.data;
      const structuredData = JSON.parse(answer);
      setStructuredText(structuredData.interview);
      setLoading('none');
    } catch (error) {
      console.error("Error parsing JSON:", error);
      setStructuredText('Er is iets fout gegaan tijdens het analyseren van de antwoorden.');
      setLoading('none');
    }
  };

  // Save questions and answers to the database
  const saveQuestions = async () => {
    structuredText.forEach(async (set, index) => {
      const questionId = uuid();
      const formId = uuid();

      await db.collection('QuestionnaireFields').doc().set({
        Compagny: client,
        CompagnyID: client,
        ID: questionId,
        Timestamp: timestamp,
        QuestionnaireID: questionaireId,
        Type: 'paragraph',
        Question: set.question,
        Explainer: '',
        ReachStart: 0,
        ReachStartLable: '',
        ReachEnd: 5,
        ReachEndLabel: '',
        Key: uuid(),
        Position: index + 1,
        Multiple: [],
        SectionID: '',
        LiveReportHidden: false,
      });

      await db.collection('QuestionnairesResponses').doc().set({
        FieldID: questionId,
        Input: set.answer,
        Timestamp: timestamp,
        CompagnyID: client,
        QuestionannaireID: questionaireId,
        MomentID: momentId,
        ResearchID: researchId,
        FormID: formId,
        ID: uuid(),
        OptionType: '',
        Persona: '',
        RowTitle: '',
        FieldType: 'paragraph',
        ColumnID: '',
      });

      setSaved(true);
    });
  };

  return (
    <div className="main">
      <div className="main-container">
        <div className="page-header">
          <h1>Ongestructureerd interview toevoegen</h1>
        </div>
        <ReadOnlyGuard edit="measurement.edit">
        <div className="table-container dashboard-container">
          <h2>Upload audio bestand</h2>
          <input type="file" id="audio" name="audio" accept="audio/*" onChange={handleFileChange} />
          {audioPreview && <audio id='audio-preview' controls src={audioPreview}></audio>}
          {audioFile && uploadProgress === 0 && !error &&
            <div>
              <button className='button-simple' onClick={handleUpload}>Upload</button>
            </div>
          }

          <UploadProgress
            progress={uploadProgress}
            error={error}
            fileName={audioFile?.name}
          />


          {/* Show transcription button if upload is complete */}
            {uploadProgress === 100 && transcriptionProgress === 0 &&
            <div>
                <button onClick={speech2Text}>Creeer transcriptie</button>
            </div>
            }
        </div>

        <div id='add-unsctructured-interviews-transcription-container'>
          <div className="table-container dashboard-container">
            <h2>Transcriptie</h2>
            {/* Display transcription progress */}
            {transcriptionProgress > 0 && (
              <ProgressBar progress={transcriptionProgress} label="Transcriberen" />
            )}
           
           {/* Display the transcription text */}
            <div id='transcription-text-container'>
                  <p>{text}</p>
            </div>

          </div>
           
          <div className="table-container dashboard-container">
            <h2>Structureren</h2>
            {text && structuredText.length === 0 && (
              <div>
               <p>Selecteer taal</p>
               <select
                   onChange={(e) => setTranscriptionLanguage(e.target.value)}
                   value={transcriptionLanguage}
                 >
                 <option value="">-- Selecteer taal --</option>
                 {languages.map((language) => (
                   <option key={language.code} value={language.code}>
                     {language.flag} {language.name}
                   </option>
                 ))}
               </select>
               <div className='button-container-left-aligned'>
                <button onClick={seperateQuestions}>Structureer text</button>
              </div>
              </div>
            )
              
            }

            { structuredText.length > 0 &&
            <div>
              <div id='transcription-text-container'>
                {Array.isArray(structuredText) && structuredText.length > 0 && structuredText.map(set => (
                  <div key={set?.question}>
                    <p><b>{set?.question}</b></p>
                    <p>{set?.answer}</p>
                  </div>
                ))}
              </div>
            </div>
            }
            
          </div>
        </div>

        {/* Save text to research */}
        {Array.isArray(structuredText) && structuredText.length > 0 && 
          <div className="table-container dashboard-container">
            <h2>Opslaan</h2>
            <h3>Selecteer meetmoment</h3>
            <select name="" id="" onChange={(e) => setMomentId(e.target.options[e.target.selectedIndex].value)} value={momentId}>
              <option value="">-- Selecteer meetmoment</option>
              {moments && moments.map(moment => (
              <div key={moment.ID}>
               <option value={moment.ID}>{moment.Title}</option>
              </div>
            ))}
            </select>
            <div className='button-container-left-aligned'>
              {saved ?
                <button className='button-simple' onClick={() =>  history.push(`/${client}/researchresponses/${questionaireId}/${researchId}`)}>Bekijk resultaten</button>
                :
                <button onClick={saveQuestions}>Opslaan</button>
              }
              
            </div>
           
          </div>
        }
        </ReadOnlyGuard>
      </div>
    </div>
  );
};

export default AddOnstructuredInterview;

